// ---------------------------------------------------------------------------
// Calculation options: date range and the original's 2 aquifer checkboxes
// ("Allow negative values in aquifer modeling" / "Model pumping", Tools.vb,
// APO_ACUIFEROS_OPC table — not typed yet, see the note in types.ts, so it's
// read here from the generic `untypedTables` catalog instead of having its
// own interface).
//
// They're not written back to the project (no export/save in this first
// version, see CLAUDE.md): they only live in session state (App.tsx), so a
// recalculation can skip repeating the dialog while the project stays open
// — see CalculationOptionsDialog.tsx and the "recalculate" button in
// TopBar.tsx.
//
// An earlier version of this dialog also had an "Accumulation" (Day/Month)
// selector, mirroring a real Ent_Config.csv key the original DLL's own
// lect_control() does read (unlike the two aquifer flags below, genuinely
// dead even in the real .exe) — but the web port's own engine.ts never grew
// the matching "aggregate the input to monthly before running" step, so it
// sat in this dialog doing nothing. Removed rather than left half-wired;
// the results panel's own "Ver por" selector (CalculationResults.tsx) is
// where viewing daily/monthly/annual actually lives now, as a display
// concern instead of a calculation one.
//
// ⚠️ CORRECTED (2026-08-24, see CLAUDE.md's engine-source audit): the
// paragraph below (kept for the record, not because it's still true)
// concluded, from the DECOMPILED Evalhid.dll, that Op_neg_acu/Op_bombeos
// were dead flags the real engine parsed and then silently ignored. With
// the real (non-decompiled) C# source (`EvalHid_Calculo`), that turned out
// to be wrong for both flags:
// - `Op_bombeos`: `Program.cs` genuinely gates the whole pumping mechanism
//   on it (`if (G.op_bomb == 1) { ...resolve/apply Ent_Bomb.csv... }` —
//   with it unchecked, `Ent_Bomb.csv` is never even opened, regardless of
//   whether the file exists or has real data). Fixed (2026-08-24):
//   `modelPumping` now genuinely gates pumping in the engine — see
//   useCalculation.ts's `seriesForCalculation`.
// - `Op_neg_acu`: it only affects the aquifer's OWN balance clamp
//   (`Acuiferos.cs:103`, `if (G.op_neg_acu==0) {...}`) — that balance
//   simulation is now ported too (`src/engine/aquifer.ts`), so
//   `negativeAquifers` is wired all the way through
//   (`CalculationResults.tsx` → `aggregateContributionsByAquifer` →
//   `calculateAquiferBalance`'s own `allowNegative` parameter).
//
// Original (now-corrected) reasoning, decompiled-DLL-based:
//
// 1. The desktop app (Form1.vb) DOES treat them as real: it reads
//    APO_ACUIFEROS_OPC and writes "Op_neg_acu"/"Op_bombeos" to
//    Ent_Config.csv, and if "Model pumping" is checked it verifies a real
//    file exists (Ent_Bomb.csv, registered as ARCHIVO_BOMBEOS in
//    APO_ARCHIVOS_SIMULACION), warning/blocking the calculation if it's
//    missing.
// 2. Evalhid.dll (the engine the desktop app actually calls, decompiled)
//    appeared to never read them: its G.lect_control() —the function that
//    parses that very Ent_Config.csv— only looked for 6 keys (Unidades/
//    Fecha_inicio/Fecha_fin/Escala_Temporal/Acumulacion/Simbolo_res_par).
//    The real (non-decompiled) source shows `lect_control()` DOES read
//    both flags (`Globales.cs:197-203`, required — error 1003 if missing)
//    — the decompiled DLL used for that earlier analysis must have been an
//    older/different build, same risk flagged elsewhere in this project
//    for other findings (Cod_modelo, aquifer aggregation, Nieve2's NaN
//    guard...).
// ---------------------------------------------------------------------------
import type { DailySeries } from "./dailySeries.ts";
import type { EvalHidProject } from "./types.ts";

export interface CalculationOptions {
  /** ISO yyyy-mm-dd, within the loaded precipitation's range. */
  startDate: string;
  endDate: string;
  negativeAquifers: boolean;
  modelPumping: boolean;
}

export function aquiferOption(project: EvalHidProject, code: number): boolean {
  const rows = project.untypedTables.get("APO_ACUIFEROS_OPC") ?? [];
  const row = rows.find((f) => Number(f.Codigo) === code);
  return row ? Boolean(row.Checked) : false;
}

/** Dialog's initial values: the whole range of the loaded precipitation, and whatever the .ehd already brought for the aquifer options. */
export function defaultCalculationOptions(
  project: EvalHidProject,
  precipitation: DailySeries
): CalculationOptions {
  return {
    startDate: precipitation.dates[0] ?? "",
    endDate: precipitation.dates[precipitation.dates.length - 1] ?? "",
    negativeAquifers: aquiferOption(project, 1),
    modelPumping: aquiferOption(project, 2),
  };
}
